import { useEffect, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";
import PdfIcon from "@/assets/icons/pdf-select.svg";
import DownloadAppStore from "@/assets/images/download-appstore.png";
import DownloadGooglePlay from "@/assets/images/download-googleplay.png";
import SoloOperatorOnboardingGuidePDF from "@/assets/templates/solo-operator-onboarding-guide.pdf";
import { Typography, SkeletonWrapper, Icon, Button, Avatar, Loading } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { useGetFleetDriversQuery } from "@/redux/apis/driver/driverApi";
import { useGetOnboardingStatusQuery, useUpdateSectionStatusMutation } from "@/redux/apis/onboarding/onboardingApi";
import { useGetUserAccountsQuery } from "@/redux/apis/userAccount/userAccountApi";
import { GettingStartedSection } from "@/redux/slices/onboarding/types";
import { copyToClipboard, getAssetUrl } from "@/utils";
import { SetupPanelItem, WalkthroughModal, ReadSetupGuideModal, WhyDoINeedModal } from "../QuickSetup/common";
import { quickSetupContent, quickSetupExplanations } from "../QuickSetup/data/quickSetupContent";
import { isSectionComplete } from "../QuickSetup/helpers/onboardingHelpers";
import { useQuickSetupNavigation } from "../QuickSetup/hooks";
import { StaticSetupPanel } from "./StaticSetupPanel";

enum ModalType {
  STRIPE_WALKTHROUGH = "stripe-walkthrough",
  STRIPE_DOCUMENTATION = "stripe-documentation",
  STRIPE_WHY = "stripe-why-do-i-need",
}

export const SoloOperatorGetStartedPanel = () => {
  const navigate = useNavigate();
  const { setQuickSetupFlag } = useQuickSetupNavigation();
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const isLoading = false;
  const operator = useOperator();
  const { providerCode, registerLinks, id: operatorId } = operator;

  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const [updateSectionStatus] = useUpdateSectionStatusMutation();

  const isStripeCompleted = onboardingStatus ? isSectionComplete(onboardingStatus.gettingStarted.stripe) : false;

  const { data: userAccountsData, isLoading: isLoadingUserAccounts } = useGetUserAccountsQuery({});
  const ownerAccount = userAccountsData?.data.items.find((item) => (item.role as string) === "Owner");

  const { data: fleetData, isLoading: isLoadingFleet } = useGetFleetDriversQuery(
    { provider_uuid: operatorId, page: "1", page_size: "1", account_status: ["active", "Approved"] },
  );
  const driver = fleetData?.data.drivers[0];
  const hasDriver = !!driver;

  const isAllComplete = isStripeCompleted && hasDriver;
  const isBannerDataLoading = isLoadingUserAccounts || isLoadingFleet;

  const [openPanels, setOpenPanels] = useState<Set<number>>(new Set([1, 2]));
  const hasInitialized = useRef(false);

  // Close both panels once we know all steps are complete
  useEffect(() => {
    if (hasInitialized.current) return;
    if (isLoadingFleet || !onboardingStatus) return;
    hasInitialized.current = true;
    if (isStripeCompleted && hasDriver) {
      setOpenPanels(new Set());
    }
  }, [isLoadingFleet, onboardingStatus, isStripeCompleted, hasDriver]);

  const handlePanelToggle = (stepNumber: number) => {
    setOpenPanels((prev) => {
      const next = new Set(prev);
      if (next.has(stepNumber)) {
        next.delete(stepNumber);
      } else {
        next.add(stepNumber);
      }
      return next;
    });
  };

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const ownerInitials = ownerAccount?.name ? getInitials(ownerAccount.name) : "";
  const driverInitials = driver?.name ? getInitials(driver.name) : "";

  const handleModalClose = () => setActiveModal(null);

  const handleStripeSetup = () => {
    setQuickSetupFlag(GettingStartedSection.STRIPE);
    navigate("/payments/payment-configuration");

    if (onboardingStatus) {
      const stripeSection = onboardingStatus.gettingStarted.stripe;
      if (stripeSection && !isSectionComplete(stripeSection)) {
        updateSectionStatus({ section: GettingStartedSection.STRIPE, completed: true }).catch((error) => {
          console.error("Failed to update section status:", error);
        });
      }
    }
  };

  const handleStripeWatchVideo = () => {
    setActiveModal(ModalType.STRIPE_WALKTHROUGH);
  };

  const handleStripeReadDocumentation = () => {
    setActiveModal(ModalType.STRIPE_DOCUMENTATION);
  };

  const handleStripeWhyDoThis = () => {
    setActiveModal(ModalType.STRIPE_WHY);
  };

  const stripeFeaturedIcon = {
    name: "stripe",
    background: "bg-[#6772E5]",
    isCustom: true,
  };

  return (
    <SkeletonWrapper isLoading={isLoading} skeleton={<div>Loading...</div>}>
      <div className="flex justify-center bg-neutral-surface-gray py-4">
        <div className="w-full max-w-[873px]">
          {/* Welcome Section */}
          <div className="mb-6 text-center">
            <Typography variant="h2" className="mb-1 text-neutral-dark-gray">
              Welcome to RideMinder 🎉
            </Typography>
            <Typography variant="paragraph" className="mb-3 text-neutral-dark-gray">
              This web portal is where you manage your account, billing, and payments.
            </Typography>
            <Typography variant="paragraph" className="text-neutral-dark-gray">
              To run jobs day-to-day — including accepting bookings, managing clients, and completing trips — you&apos;ll use the RideMinder
              Driver App on your mobile device.
            </Typography>
          </div>

          <div className="flex flex-col gap-5">
            {/* All Steps Complete Banner */}
            {isAllComplete && (
              <div className="relative rounded-lg border border-neutral-mid-gray bg-white p-6">
                {isBannerDataLoading && <Loading className="rounded-lg" />}
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-success bg-success-light">
                    <span className="text-2xl">🎉</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Typography variant="title" className="text-black">
                      You&apos;re all set!
                    </Typography>
                    <Icon isCustom name="check" size="md" className="text-success" />
                  </div>
                </div>
                <Typography variant="paragraph" className="mb-5 text-neutral-dark-gray">
                  Your account is fully configured. You can now accept bookings and get paid through the RideMinder Driver App.
                </Typography>
                <div className="grid gap-3 md:grid-cols-2">
                  {/* Operator Web Portal Card */}
                  <div className="rounded-lg border border-neutral-mid-gray p-4">
                    <Typography variant="small" className="mb-3 uppercase text-neutral-silver-gray">
                      Operator Web Portal
                    </Typography>
                    <div className="mb-3 flex items-center gap-3">
                      <Avatar size="md" className="bg-primary text-white">
                        <Typography variant="action">{ownerInitials}</Typography>
                      </Avatar>
                      <div className="flex flex-col">
                        <Typography variant="action" className="text-black">
                          {ownerAccount?.name}
                        </Typography>
                        <Typography variant="small-regular" className="text-neutral-silver-gray">
                          Owner
                        </Typography>
                      </div>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2">
                      <div>
                        <Typography variant="small" className="uppercase text-neutral-silver-gray">
                          Web App Login Email
                        </Typography>
                        <Typography variant="paragraph" className="text-black">
                          {ownerAccount?.email}
                        </Typography>
                      </div>
                      <button onClick={() => copyToClipboard(ownerAccount?.email ?? "")} className="p-1 text-neutral-dark-gray hover:text-neutral-black">
                        <Icon name="Copy" size="lg" />
                      </button>
                    </div>
                  </div>

                  {/* Driver App Card */}
                  {driver && (
                    <div className="rounded-lg border border-neutral-mid-gray p-4">
                      <Typography variant="small" className="mb-3 uppercase text-neutral-silver-gray">
                        Driver App
                      </Typography>
                      <div className="mb-3 flex items-center gap-3">
                        <Avatar size="md" src={driver.avatar ? getAssetUrl(driver.avatar) : undefined} className="bg-neutral-mid-gray text-neutral-dark-gray">
                          {!driver.avatar && <Typography variant="action">{driverInitials}</Typography>}
                        </Avatar>
                        <div className="flex flex-col">
                          <Typography variant="action" className="text-black">
                            {driver.name}
                          </Typography>
                          <Typography variant="small-regular" className="text-neutral-silver-gray">
                            Driver
                          </Typography>
                        </div>
                      </div>
                      <div className="flex items-center justify-between rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray px-3 py-2">
                        <div>
                          <Typography variant="small" className="uppercase text-neutral-silver-gray">
                            Driver Login Email
                          </Typography>
                          <Typography variant="paragraph" className="text-black">
                            {driver.email}
                          </Typography>
                        </div>
                        <button onClick={() => copyToClipboard(driver.email)} className="p-1 text-neutral-dark-gray hover:text-neutral-black">
                          <Icon name="Copy" size="lg" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Setup Payments Panel */}
            <StaticSetupPanel
              stepNumber={1}
              title="Setup Payments"
              description="Connect your Stripe account to start accepting payments"
              stepCount={1}
              completedSteps={isStripeCompleted ? 1 : 0}
              isOpen={openPanels.has(1)}
              onToggle={() => handlePanelToggle(1)}
            >
              <SetupPanelItem
                icon={stripeFeaturedIcon.name}
                iconBackground={stripeFeaturedIcon.background}
                isCustomIcon={stripeFeaturedIcon.isCustom}
                title="Connect Stripe to get paid"
                isCompleted={isStripeCompleted}
                actions={[
                  {
                    label: "Setup Stripe",
                    onClick: handleStripeSetup,
                    variant: "primary",
                  },
                ]}
                helpOptions={[
                  {
                    label: "Why do I need to do this?",
                    icon: <Icon name="help-circle" size="md" isCustom variant="Outline" />,
                    onClick: handleStripeWhyDoThis,
                  },
                  { label: "Watch walkthrough video", icon: "Video", onClick: handleStripeWatchVideo },
                  { label: "Read Setup Guide", icon: "Document", onClick: handleStripeReadDocumentation },
                ]}
              />
            </StaticSetupPanel>

            {/* Download the RideMinder Driver App Panel */}
            <StaticSetupPanel
              stepNumber={2}
              title="Download the RideMinder Driver App"
              description="Download the app and log in to start managing your bookings"
              stepCount={2}
              completedSteps={hasDriver ? 2 : 0}
              isOpen={openPanels.has(2)}
              onToggle={() => handlePanelToggle(2)}
            >
              <div className="grid gap-3 md:grid-cols-2">
                {/* Left Column - Download */}
                <div className="flex gap-3 rounded-lg border border-neutral-mid-gray bg-white p-6">
                  <Typography variant="title" className="shrink-0 font-semibold text-neutral-dark-gray">
                    1
                  </Typography>
                  <div className="flex flex-1 flex-col gap-3">
                    <div>
                      <Typography variant="action" className="text-neutral-dark-gray">
                        Download the RideMinder Driver App
                      </Typography>
                      <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
                        This is your <span className="font-semibold">main work app</span>, used on your phone to run jobs day-to-day.
                      </Typography>
                    </div>

                    <Typography variant="paragraph" className="text-neutral-dark-gray">
                      Download it from the App Store or Google Play on your mobile device.
                    </Typography>

                    <div className="flex gap-3">
                      <a href={registerLinks?.driver} target="_blank" rel="noopener noreferrer">
                        <img src={DownloadAppStore} alt="Download on the App Store" className="w-[120px]" />
                      </a>
                      <a href={registerLinks?.driver} target="_blank" rel="noopener noreferrer">
                        <img src={DownloadGooglePlay} alt="Get it on Google Play" className="w-[135px]" />
                      </a>
                    </div>

                    <Typography variant="paragraph" className="text-neutral-dark-gray">
                      or Scan the QR to download
                    </Typography>

                    {registerLinks?.driver && (
                      <div className="w-fit">
                        <QRCode value={registerLinks.driver} style={{ height: "125px", width: "125px" }} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Log in */}
                <div className="flex gap-3 rounded-lg border border-neutral-mid-gray bg-white p-6">
                  <Typography variant="title" className="shrink-0 font-semibold text-neutral-dark-gray">
                    2
                  </Typography>
                  <div className="flex flex-1 flex-col gap-3">
                    <div>
                      <Typography variant="action" className="text-neutral-dark-gray">
                        Log in to the Driver App
                      </Typography>
                      <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
                        The Driver App uses a <span className="font-semibold">separate login</span> from this web portal.
                      </Typography>
                    </div>

                    <Typography variant="paragraph" className="text-neutral-dark-gray">
                      Choose one option in the app:
                    </Typography>

                    <div className="flex flex-col gap-3">
                      <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-4">
                        <Typography variant="action" className="mb-2.5 text-neutral-dark-gray">
                          Option 1: Log in with an existing Driver account
                        </Typography>
                        <Typography variant="paragraph" className="mb-4 text-neutral-dark-gray">
                          Use this if you&apos;ve driven with RideMinder before and want to use that same account.
                        </Typography>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Transport Provider Code: <span className="font-semibold uppercase text-neutral-dark-gray">{providerCode}</span>
                        </Typography>
                      </div>

                      <div className="rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-4">
                        <Typography variant="action" className="mb-2.5 text-neutral-dark-gray">
                          Option 2: Create a New Driver Account
                        </Typography>
                        <Typography variant="paragraph" className="mb-4 text-neutral-dark-gray">
                          Create a new profile directly in the app.
                        </Typography>
                        <Typography variant="paragraph" className="mb-4 text-neutral-dark-gray">
                          You&apos;ll be prompted to use a different email from your Solo Operator web account.
                        </Typography>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Transport Provider Code: <span className="font-semibold uppercase text-neutral-dark-gray">{providerCode}</span>
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </StaticSetupPanel>

            {/* How you'll use RideMinder as a Solo Operator */}
            <div className="flex flex-col">
              <Typography variant="title" className="mb-4 text-neutral-dark-gray">
                How you&apos;ll use RideMinder as a Solo Operator
              </Typography>

              <div className="overflow-hidden rounded-lg border border-neutral-mid-gray">
                <div className="grid md:grid-cols-2">
                  {/* Driver App Card */}
                  <div className="flex flex-col gap-4 border-b border-neutral-mid-gray p-6 md:border-b-0 md:border-r">
                    <div className="aspect-square flex w-fit items-center justify-center rounded bg-primary-light py-1 pl-2 pr-0">
                      <Icon name="Mobile" size="lg" className="text-primary" />
                    </div>

                    <Typography variant="action" className="text-neutral-dark-gray">
                      Driver App (Mobile)
                    </Typography>

                    <Typography variant="paragraph" className="text-neutral-dark-gray">
                      This is where you&apos;ll do <span className="font-semibold">all day-to-day work.</span>
                    </Typography>

                    <Typography variant="paragraph" className="text-neutral-dark-gray">
                      Use the Driver App on your phone to:
                    </Typography>

                    <ul className="ml-4 list-disc space-y-1">
                      <li>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Manage job bookings
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Manage clients and invitations
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Manage your private networks
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Manage your vehicle
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Update your trip pricing
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Close and complete job bookings
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          View your payouts
                        </Typography>
                      </li>
                    </ul>

                    <Typography variant="paragraph" className="text-neutral-dark-gray">
                      Accessed on your mobile device
                    </Typography>
                  </div>

                  {/* Web Portal Card */}
                  <div className="flex flex-col gap-4 p-6">
                    <div className="aspect-square flex w-fit items-center justify-center rounded bg-primary-light p-1">
                      <Icon name="Global" size="lg" className="text-primary" />
                    </div>

                    <Typography variant="action" className="text-neutral-dark-gray">
                      Web Portal (You&apos;re here now)
                    </Typography>

                    <Typography variant="paragraph" className="text-neutral-dark-gray">
                      You&apos;re currently logged into your <span className="font-semibold">Solo Operator web portal.</span>
                    </Typography>

                    <Typography variant="paragraph" className="text-neutral-dark-gray">
                      Use this portal to:
                    </Typography>

                    <ul className="ml-4 list-disc space-y-1">
                      <li>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Manage your subscription
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          Set up and manage payments
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="paragraph" className="text-neutral-dark-gray">
                          View billing and invoices
                        </Typography>
                      </li>
                    </ul>

                    <Typography variant="paragraph" className="text-neutral-dark-gray">
                      All bookings and job management happen in the <span className="font-semibold">Driver App on your mobile.</span>
                    </Typography>

                    <div className="mt-auto rounded-lg border border-neutral-mid-gray bg-neutral-gray p-4">
                      <Typography variant="paragraph" className="text-sm text-neutral-dark-gray">
                        Jobs you offload include a 20% operator commission, paid to you and visible in payout history
                      </Typography>
                    </div>
                  </div>
                </div>

                {/* Download PDF Section */}
                <div className="flex items-center justify-between border-t border-neutral-mid-gray bg-neutral-surface-gray px-6 py-4">
                  <div className="flex items-center gap-3">
                    <img src={PdfIcon} alt="PDF" className="h-10 w-10" />
                    <div className="flex flex-col">
                      <Typography variant="action" className="text-neutral-dark-gray">
                        Complete Onboarding Guide
                      </Typography>
                      <Typography variant="small" className="text-neutral-dark-gray">
                        Step-by-step walkthrough for Solo operators &middot; PDF
                      </Typography>
                    </div>
                  </div>
                  <a href={SoloOperatorOnboardingGuidePDF} target="_blank" rel="noreferrer">
                    <Button variant="secondary" size="sm" startIcon="ImportCurve">
                      Download PDF
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stripe Modals */}
      <WalkthroughModal
        featuredIcon={stripeFeaturedIcon}
        open={activeModal === ModalType.STRIPE_WALKTHROUGH}
        onClose={handleModalClose}
        title="Video Walkthrough (Stripe Setup)"
        videoUrl={quickSetupContent.stripe.setup.videoUrl}
      />

      <ReadSetupGuideModal
        featuredIcon={stripeFeaturedIcon}
        open={activeModal === ModalType.STRIPE_DOCUMENTATION}
        onClose={handleModalClose}
        title="Stripe Setup Guide"
        documentationUrl={quickSetupContent.stripe.setup.documentationUrl}
        pdfUrl={quickSetupContent.stripe.setup.pdfUrl}
      />

      <WhyDoINeedModal
        featuredIcon={stripeFeaturedIcon}
        open={activeModal === ModalType.STRIPE_WHY}
        onClose={handleModalClose}
        title="Why do I need to setup Stripe?"
        explanations={quickSetupExplanations.stripe.setup}
      />
    </SkeletonWrapper>
  );
};
